import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, Navigate, NavLink, Outlet, useNavigate, useParams } from 'react-router-dom';
import classNames from 'classnames';

import { PermissionKeys, SpaceDiscriminator, useGetSpacesByIdQuery, useGetBuildingsByIdQuery } from '@client/api';
import {
  isDefinedString,
  PageDetailHeader,
  PageNavigation,
  Skeleton,
  Text,
  useRouteDocumentTitle,
} from '@client/toolkit';

import { SpaceDeleteModal } from './components/space-delete-modal.component';
import { SpaceDuplicateModal } from './components/space-duplicate-modal.component';
import { SpaceEditModal } from './components/space-edit-modal.component';

import { PortfolioPaths } from '../../portfolio.router';
import { SpaceContext } from '../../context/space-context';
import { SpacePermissionBasedCta } from '../../components/space-permission-based-cta/space-permission-based-cta';
import { PageContentAnimation } from '../../../../components/layout/page-content-animation';
import { useBuildingPackages } from '../../../../hooks/useBuildingPackages';
import { useSpacePermissions } from '../../../../hooks/useSpacePermissions/useSpacePermissions';

export const SpaceRoute = () => {
  const { t } = useTranslation('rentalObjects');
  const { t: tCommon } = useTranslation();
  const { t: tGlobal } = useTranslation('global');
  const { t: tType } = useTranslation('type');

  const navigate = useNavigate();

  const { spaceId, buildingId, discriminator } = useParams<{
    buildingId: string;
    spaceId: string;
    discriminator: SpaceDiscriminator;
  }>();

  useRouteDocumentTitle(t('documentTitle'), PortfolioPaths.UnspecificSpace);

  const { data: building } = useGetBuildingsByIdQuery({ id: buildingId ?? '' }, { skip: !buildingId });
  const { hasManagement, isPortfolioOnly } = useBuildingPackages(building?.packages);

  const { hasAnyPermission: hasSpacePermission, isLoading: isLoadingSpacePermissions } = useSpacePermissions({
    id: spaceId,
  });
  const hasUpdatePermission = isLoadingSpacePermissions || hasSpacePermission([PermissionKeys.Space.Update]);
  // Owner/Manager/Creator/Investor are exactly the roles PermissionKeys.Space.FinancialView is
  // granted to, so checking this permission also covers inherited access correctly - the roles
  // array reports "Inherited" for those, not the underlying role, so checking roles directly
  // would incorrectly hide these tabs.
  const hasFinancialViewPermission =
    isLoadingSpacePermissions || hasSpacePermission([PermissionKeys.Space.FinancialView]);

  const {
    data: space,
    currentData: currentSpace,
    isFetching,
    isLoading,
    isError,
  } = useGetSpacesByIdQuery(
    {
      id: spaceId ?? '',
    },
    { skip: !spaceId },
  );

  const [editing, setEditing] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [duplicating, setDuplicating] = useState<boolean>(false);

  const isGroup = discriminator === 'Generic';

  const tabs = useMemo(() => {
    const baseTabs = [
      {
        key: 'dashboard',
        label: tGlobal('summary'),
        to: generatePath(PortfolioPaths.Space, { buildingId, spaceId, discriminator }),
        end: true,
      },
    ];

    if (isGroup && hasUpdatePermission) {
      baseTabs.push({
        key: 'arealer',
        label: tGlobal('entity.area', { context: 'plural' }),
        to: generatePath(PortfolioPaths.SpaceArealer, { buildingId, spaceId, discriminator }),
        end: false,
      });
    }

    if (hasManagement && hasFinancialViewPermission) {
      baseTabs.push({
        key: 'agreements',
        label: tGlobal('entity.agreement', { context: 'plural' }),
        to: generatePath(PortfolioPaths.SpaceAgreements, { buildingId, spaceId, discriminator }),
        end: false,
      });
    }

    const showAgreementParties =
      (!isPortfolioOnly || discriminator === 'CommercialSpace') && hasFinancialViewPermission;

    if (showAgreementParties) {
      baseTabs.push({
        key: 'agreementParties',
        label: tGlobal('entity.tenant', { context: 'plural' }),
        to: generatePath(PortfolioPaths.SpaceAgreementParties, { buildingId, spaceId, discriminator }),
        end: false,
      });
    }

    if (hasFinancialViewPermission) {
      baseTabs.push({
        key: 'roles',
        label: tGlobal('entity.roles'),
        to: generatePath(PortfolioPaths.SpaceRoles, { buildingId, spaceId, discriminator }),
        end: false,
      });
    }

    if (!isGroup && hasUpdatePermission) {
      baseTabs.push({
        key: 'associations',
        label: tGlobal('space.associations'),
        to: generatePath(PortfolioPaths.SpaceAssociations, { buildingId, spaceId, discriminator }),
        end: false,
      });
    }

    return baseTabs;
  }, [
    tGlobal,
    buildingId,
    spaceId,
    discriminator,
    isGroup,
    hasManagement,
    isPortfolioOnly,
    hasUpdatePermission,
    hasFinancialViewPermission,
  ]);

  const handleOpenEdit = () => {
    setEditing(true);
  };

  const handleCloseEdit = () => {
    setEditing(false);
  };

  const handleOpenDelete = () => {
    setDeleting(true);
  };

  const handleCloseDelete = () => {
    setDeleting(false);
  };

  const handleOpenDuplicate = () => {
    setDuplicating(true);
  };

  const handleCloseDuplicate = () => {
    setDuplicating(false);
  };

  const handleDeleteFulfilled = () => {
    setDeleting(false);

    if (space?.parent == null || !isDefinedString(space.parent.id)) {
      navigate(generatePath(PortfolioPaths.Building, { buildingId }), { replace: true });
      return;
    }
    if (space.parent.discriminator === 'Building') {
      navigate(generatePath(PortfolioPaths.Building, { buildingId }), { replace: true });
      return;
    }

    navigate(
      generatePath(PortfolioPaths.Space, {
        buildingId,
        spaceId: space.parent.id,
        discriminator: space.parent.discriminator,
      }),
      { replace: true },
    );
  };

  if (!isLoading && isError) {
    return <Navigate to={generatePath(PortfolioPaths.Building, { buildingId })} replace />;
  }

  return (
    <SpaceContext.Provider value={{ space: space, isFetching }}>
      <PageNavigation className="mb-2" />

      <PageDetailHeader align="left">
        <PageContentAnimation>
          <div className="mb-4">
            <div className="text-gray-400 font-medium text-sm">
              {discriminator == null && isFetching ? (
                <Skeleton className="w-[25%]" />
              ) : discriminator || currentSpace?.discriminator ? (
                tType(`SpaceDiscriminator.${discriminator || currentSpace?.discriminator}`)
              ) : (
                '-'
              )}
            </div>
            <Text
              className="text-xl font-medium"
              isEmpty={!isDefinedString(space?.name)}
              loading={isFetching}
              SkeletonProps={{ className: 'w-[35%]' }}
            >
              {space?.name}
            </Text>
          </div>
          <div className="flex items-end">
            <div className="flex gap-10 border-b border-border flex-1">
              {tabs.map((tab, index) => (
                <NavLink
                  key={tab.key}
                  to={tab.to}
                  end={tab.end}
                  replace
                  className={({ isActive }) =>
                    classNames(
                      'pb-2 text-sm font-medium transition-colors relative whitespace-nowrap',
                      index === 0 ? 'ml-4' : '',
                      isActive
                        ? 'text-brand-primary after:absolute after:bottom-0 after:h-0.5 after:bg-brand-primary after:right-[-1.25rem] ' +
                            (index === 0 ? 'after:left-[-1rem]' : 'after:left-[-1.25rem]')
                        : 'text-white hover:text-brand-primary',
                    )
                  }
                >
                  {tab.label}
                </NavLink>
              ))}
            </div>
            <div className="flex space-x-2 items-center ml-4">
              {space && (
                <SpacePermissionBasedCta
                  space={space}
                  loading={isFetching}
                  handleEditClick={handleOpenEdit}
                  handleDeleteClick={handleOpenDelete}
                  handleDuplicateClick={handleOpenDuplicate}
                  canDuplicate={!!space.parent?.id}
                  editModal={
                    <SpaceEditModal open={editing} onClose={handleCloseEdit} onAfterSubmit={handleCloseEdit} />
                  }
                  deleteModal={
                    <SpaceDeleteModal
                      space={space}
                      open={deleting}
                      onCancel={handleCloseDelete}
                      onDeleteFulfilled={handleDeleteFulfilled}
                    />
                  }
                  duplicateModal={
                    <SpaceDuplicateModal
                      open={duplicating}
                      onClose={handleCloseDuplicate}
                      onAfterSubmit={handleCloseDuplicate}
                      spaceId={spaceId}
                      discriminator={discriminator}
                      buildingId={buildingId}
                    />
                  }
                />
              )}
            </div>
          </div>
        </PageContentAnimation>
      </PageDetailHeader>
      <Outlet />
    </SpaceContext.Provider>
  );
};
